import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { useTheme } from 'styled-components';
import { unwrapResult } from '@reduxjs/toolkit';

import { Button, ButtonVariant } from '@/core/components/Button';
import { Heading, Paragraph } from '@/core/components/Typography';
import { Icon } from '@/core/components/Icons';
import { Switch } from '@/core/components/Switch';
import { useAppDispatch } from '@/core/store/store';
import { ReportImpact, ReportTypeCategory } from '@/core/interfaces/common';

import { getSelectedFocusSelector } from '@/features/Focus/store';
import {
  ActionButton,
  ActionsRow,
  FooterRow,
  HeaderRow,
  Label,
  MetadataToggleRow,
  PageWrapper,
  Section,
  TitleInput,
} from '@/features/EditAnalysis/EditAnalysis.styles';
import type { UpdateAnalysisPayload } from '@/features/ChatBot/interfaces';
import {
  getAnalysesList,
  getAnalysis,
  updateAnalysis,
  deleteAnalysis,
} from '@/features/ChatBot/store';

import { markdownToHtml } from '@/utils/markdownToHtml';
import { htmlToMarkdown } from '@/utils/htmlToMarkdown';

import { DeleteAnalysisConfirmModal } from './components/DeleteAnalysisConfirmModal';
import { MetadataSection } from './components/MetadataSection';
import { WysiwygEditor } from './components/WysiwygEditor';
import { EditAnalysisFormData } from './interfaces';

const DEFAULT_FORM_VALUES: EditAnalysisFormData = {
  title: '',
  description: '',
  assignMetadata: false,
  analysisFocusId: null,
  eventDate: '',
  eventTime: '',
  eventEndDate: '',
  eventEndTime: '',
  categoryIds: [],
  impact: ReportImpact.EXTREME,
  location: '',
  locationCoordinates: null,
  countryIds: [],
};

type EditAnalysisProps = {
  analysisId?: number | null;
  onClose?: () => void;
  onDeleteSuccess?: () => void;
};

export const EditAnalysis = ({
  analysisId = null,
  onClose,
  onDeleteSuccess,
}: EditAnalysisProps) => {
  const dispatch = useAppDispatch();
  const selectedFocus = useSelector(getSelectedFocusSelector);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [isLoadingAnalysis, setIsLoadingAnalysis] = useState(false);
  const { colors } = useTheme();

  const form = useForm<EditAnalysisFormData>({
    defaultValues: DEFAULT_FORM_VALUES,
  });

  const { register, setValue, reset, watch, handleSubmit } = form;
  const assignMetadata = watch('assignMetadata');

  useEffect(() => {
    if (analysisId == null) {
      reset(DEFAULT_FORM_VALUES);
      setIsLoadingAnalysis(false);

      return;
    }

    let cancelled = false;

    setIsLoadingAnalysis(true);

    void (async () => {
      try {
        const data = unwrapResult(await dispatch(getAnalysis(analysisId)));

        if (cancelled) return;

        const meta = data.meta;
        const firstLoc = meta?.locations?.[0];
        const hasMetadata =
          meta != null &&
          ((meta.categories?.length ?? 0) > 0 ||
            (meta.countries?.length ?? 0) > 0 ||
            meta.eventDate != null ||
            meta.eventTime != null ||
            meta.eventEndDate != null ||
            meta.eventEndTime != null ||
            (Array.isArray(meta.locations) && meta.locations.length > 0) ||
            (data.location ?? meta.locations?.[0]?.address) != null ||
            meta.impact != null);

        reset({
          title: data.title ?? '',
          description: markdownToHtml(data.description ?? ''),
          assignMetadata: !!hasMetadata,
          analysisFocusId: data.focus ?? null,
          eventDate: meta?.eventDate ?? '',
          eventTime: meta?.eventTime ?? '',
          eventEndDate: meta?.eventEndDate ?? '',
          eventEndTime: meta?.eventEndTime ?? '',
          categoryIds: meta?.categories?.map(category => category.id) ?? [],
          impact: meta?.impact ?? ReportImpact.EXTREME,
          location: data.location ?? firstLoc?.address ?? '',
          locationCoordinates:
            firstLoc &&
            typeof firstLoc.latitude === 'number' &&
            typeof firstLoc.longitude === 'number'
              ? {
                  lat: firstLoc.latitude,
                  lng: firstLoc.longitude,
                }
              : null,
          countryIds: meta?.countries?.map(country => country.id) ?? [],
        });
      } catch {
        if (!cancelled) reset(DEFAULT_FORM_VALUES);
      } finally {
        if (!cancelled) setIsLoadingAnalysis(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [analysisId, dispatch, reset]);

  const onAssignMetadataChange = useCallback(() => {
    setValue('assignMetadata', !assignMetadata);
  }, [assignMetadata, setValue]);

  const openDeleteConfirm = useCallback(() => {
    if (analysisId == null) return;
    setIsDeleteConfirmOpen(true);
  }, [analysisId]);

  const closeDeleteConfirm = useCallback(() => {
    if (!isDeleting) setIsDeleteConfirmOpen(false);
  }, [isDeleting]);

  const confirmDelete = useCallback(async () => {
    if (analysisId == null) return;
    setIsDeleting(true);

    try {
      unwrapResult(await dispatch(deleteAnalysis(analysisId)));
      dispatch(getAnalysesList());
      setIsDeleteConfirmOpen(false);

      if (onDeleteSuccess) {
        onDeleteSuccess();
      } else {
        onClose?.();
      }
    } finally {
      setIsDeleting(false);
    }
  }, [analysisId, dispatch, onClose, onDeleteSuccess]);

  const onSubmit = useCallback(
    async (data: EditAnalysisFormData) => {
      if (analysisId == null) return;
      const focusId = data.analysisFocusId ?? selectedFocus?.id ?? 0;

      if (focusId === 0) return;

      const payload: UpdateAnalysisPayload = {
        title: data.title,
        description: htmlToMarkdown(data.description),
        meta: {
          reportType: ReportTypeCategory.ANALYSIS,
          eventDate: data.eventDate || undefined,
          eventTime: data.eventTime || undefined,
          eventEndDate: data.eventEndDate || undefined,
          eventEndTime: data.eventEndTime || undefined,
          impact: data.impact,
          categories: data.categoryIds,
          countries: data.countryIds,
          locations: data.location?.trim()
            ? [
                {
                  latitude: data.locationCoordinates?.lat ?? 0,
                  longitude: data.locationCoordinates?.lng ?? 0,
                  address: data.location.trim(),
                },
              ]
            : [],
        },
        focus: focusId,
        status: 'Draft',
      };

      unwrapResult(
        await dispatch(
          updateAnalysis({
            analysisId,
            payload,
          })
        )
      );
      dispatch(getAnalysesList());
      await dispatch(getAnalysis(analysisId));
      onClose?.();
    },
    [analysisId, dispatch, onClose, selectedFocus?.id]
  );

  return (
    <FormProvider {...form}>
      <PageWrapper
        as="form"
        onSubmit={handleSubmit(onSubmit)}
      >
        <HeaderRow>
          <Heading type="h2">Edit analysis</Heading>
          <ActionsRow>
            <ActionButton
              type="button"
              disabled={analysisId == null || isDeleting}
              onClick={openDeleteConfirm}
            >
              <Icon
                name="Trash"
                size={14}
                color={colors.warning}
              />
              Delete analysis
            </ActionButton>
          </ActionsRow>
        </HeaderRow>

        <Section>
          <Label>Title</Label>
          {isLoadingAnalysis ? (
            <Paragraph type="normal">Loading analysis…</Paragraph>
          ) : (
            <TitleInput
              {...register('title')}
              placeholder="Analysis title"
            />
          )}
        </Section>

        <Section>
          <Label>Description</Label>
          {!isLoadingAnalysis && (
            <Controller
              name="description"
              control={form.control}
              render={({ field }) => (
                <WysiwygEditor
                  value={field.value}
                  onChange={field.onChange}
                  placeholder="Write the analysis description..."
                />
              )}
            />
          )}
        </Section>

        <Section>
          <MetadataToggleRow>
            <Switch
              isChecked={assignMetadata}
              onChange={onAssignMetadataChange}
            />
            <Label>Assign metadata</Label>
          </MetadataToggleRow>
          {assignMetadata ? (
            <>
              <Paragraph type="normal">
                Provide specific data such as category, country, location, impact, timeframe etc.
              </Paragraph>
              <MetadataSection />
            </>
          ) : null}
        </Section>
        <FooterRow>
          <Button
            type="submit"
            disabled={analysisId == null || form.formState.isSubmitting || isLoadingAnalysis}
          >
            {form.formState.isSubmitting ? 'Saving…' : 'SAVE CHANGES'}
          </Button>
          <Button
            type="button"
            variant={ButtonVariant.TERTIARY}
            onClick={onClose}
          >
            Cancel
          </Button>
        </FooterRow>
        <DeleteAnalysisConfirmModal
          isOpen={isDeleteConfirmOpen}
          onCancel={closeDeleteConfirm}
          onConfirm={confirmDelete}
          isDeleting={isDeleting}
        />
      </PageWrapper>
    </FormProvider>
  );
};
